import React, { useState, useEffect, useCallback } from 'react';
import {
    Table, Button, Modal, Form, Input, InputNumber, Select,
    Space, Tag, Switch, Typography, message, Popconfirm, Tooltip,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import AdminLayout from '../Layout/AdminLayout';
import axiosInstance from '../../utils/axiosConfig';

const { Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 5 }, (_, i) => CURRENT_YEAR + 1 - i);

const METHOD_COLS = [
    { key: 'diem_thpt', label: 'THPT',  max: 30 },
    { key: 'diem_hb',   label: 'HB',    max: 30 },
    { key: 'diem_tsa',  label: 'TSA',   max: 100 },
    { key: 'diem_hsa',  label: 'HSA',   max: 150 },
    { key: 'diem_spt',  label: 'SPT',   max: 30 },
];

const AdminAdmissionPrograms = () => {
    const [programs, setPrograms]   = useState([]);
    const [loading, setLoading]     = useState(false);
    const [filterYear, setFilterYear] = useState(CURRENT_YEAR);

    const [modalOpen, setModalOpen] = useState(false);
    const [editRecord, setEditRecord] = useState(null);
    const [saving, setSaving]       = useState(false);

    const [form] = Form.useForm();

    const fetchData = useCallback(async (year) => {
        setLoading(true);
        try {
            const res = await axiosInstance.get('/api/v1/admin/admission-programs', {
                params: { year },
            });
            setPrograms(res.data?.data ?? []);
        } catch {
            message.error('Không thể tải dữ liệu. Vui lòng thử lại.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchData(filterYear); }, [fetchData, filterYear]);

    const openAdd = () => {
        setEditRecord(null);
        form.resetFields();
        form.setFieldsValue({ nam_tuyen_sinh: filterYear, is_new: false, is_active: true });
        setModalOpen(true);
    };

    const openEdit = (record) => {
        setEditRecord(record);
        form.setFieldsValue({
            ma_xt:            record.ma_xt,
            ten_chuyen_nganh: record.ten_chuyen_nganh,
            ma_nganh:         record.ma_nganh,
            ten_nganh:        record.ten_nganh,
            diem_thpt:        record.diem_thpt,
            diem_hb:          record.diem_hb,
            diem_tsa:         record.diem_tsa,
            diem_hsa:         record.diem_hsa,
            diem_spt:         record.diem_spt,
            to_hop_xt:        record.to_hop_xt,
            is_new:           record.is_new,
            is_active:        record.is_active,
            nam_tuyen_sinh:   record.nam_tuyen_sinh,
        });
        setModalOpen(true);
    };

    const handleSave = async () => {
        try {
            const values = await form.validateFields();
            setSaving(true);
            if (editRecord) {
                await axiosInstance.put(`/api/v1/admin/admission-programs/${editRecord.id}`, values);
                message.success('Đã cập nhật ngành.');
            } else {
                await axiosInstance.post('/api/v1/admin/admission-programs', values);
                message.success('Đã thêm ngành.');
            }
            setModalOpen(false);
            fetchData(filterYear);
        } catch (err) {
            if (err?.response?.data?.errors) {
                message.error('Kiểm tra lại thông tin nhập vào.');
            }
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id) => {
        try {
            await axiosInstance.delete(`/api/v1/admin/admission-programs/${id}`);
            message.success('Đã xóa ngành.');
            fetchData(filterYear);
        } catch {
            message.error('Xóa thất bại.');
        }
    };

    const columns = [
        {
            title: 'Mã XT',
            dataIndex: 'ma_xt',
            width: 130,
            fixed: 'left',
            render: (v, r) => (
                <Space direction="vertical" size={0}>
                    <Text strong style={{ fontSize: 12 }}>{v}</Text>
                    {r.is_new && <Tag color="purple" style={{ fontSize: 10 }}>Ngành mới</Tag>}
                    {!r.is_active && <Tag color="default" style={{ fontSize: 10 }}>Ẩn</Tag>}
                </Space>
            ),
        },
        {
            title: 'Tên chuyên ngành',
            dataIndex: 'ten_chuyen_nganh',
            ellipsis: true,
            render: (v, r) => (
                <Tooltip title={r.ma_nganh ? `Mã ngành: ${r.ma_nganh}` : ''}>
                    <span>{v}</span>
                </Tooltip>
            ),
        },
        ...METHOD_COLS.map(({ key, label }) => ({
            title: label,
            dataIndex: key,
            width: 70,
            align: 'center',
            render: (v) => v != null ? <Text style={{ fontSize: 12 }}>{Number(v).toFixed(2)}</Text> : <Text type="secondary">—</Text>,
        })),
        {
            title: 'Tổ hợp',
            dataIndex: 'to_hop_xt',
            width: 160,
            ellipsis: true,
            render: (v) => v || <Text type="secondary">—</Text>,
        },
        {
            title: 'Năm',
            dataIndex: 'nam_tuyen_sinh',
            width: 70,
            align: 'center',
        },
        {
            title: '',
            key: 'actions',
            width: 90,
            fixed: 'right',
            render: (_, record) => (
                <Space>
                    <Button
                        type="text"
                        icon={<EditOutlined />}
                        size="small"
                        onClick={() => openEdit(record)}
                    />
                    <Popconfirm
                        title="Xóa ngành này?"
                        onConfirm={() => handleDelete(record.id)}
                        okText="Xóa"
                        cancelText="Hủy"
                    >
                        <Button type="text" danger icon={<DeleteOutlined />} size="small" />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <AdminLayout title="Quản lý ngành – Đánh giá cơ hội">
            <Space style={{ marginBottom: 16 }} wrap>
                <Select
                    value={filterYear}
                    style={{ width: 120 }}
                    onChange={setFilterYear}
                >
                    {YEAR_OPTIONS.map(y => <Option key={y} value={y}>{y}</Option>)}
                </Select>
                <Button type="primary" icon={<PlusOutlined />} onClick={openAdd}>
                    Thêm ngành
                </Button>
                {programs.length > 0 && (
                    <Text type="secondary">{programs.length} ngành</Text>
                )}
            </Space>

            <Table
                columns={columns}
                dataSource={programs}
                rowKey="id"
                loading={loading}
                size="small"
                scroll={{ x: 900 }}
                pagination={{ pageSize: 30, showTotal: (t) => `Tổng ${t} ngành` }}
            />

            <Modal
                title={editRecord ? 'Sửa ngành' : 'Thêm ngành'}
                open={modalOpen}
                onOk={handleSave}
                onCancel={() => setModalOpen(false)}
                confirmLoading={saving}
                okText={editRecord ? 'Cập nhật' : 'Thêm'}
                cancelText="Hủy"
                destroyOnClose
                width={680}
            >
                <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
                    <Space style={{ width: '100%' }} size={12}>
                        <Form.Item
                            name="ma_xt"
                            label="Mã xét tuyển"
                            rules={[{ required: true, message: 'Bắt buộc' }]}
                            style={{ width: 180 }}
                        >
                            <Input placeholder="VD: GTADCEN2" disabled={!!editRecord} />
                        </Form.Item>
                        <Form.Item
                            name="nam_tuyen_sinh"
                            label="Năm tuyển sinh"
                            rules={[{ required: true }]}
                            style={{ width: 130 }}
                        >
                            <Select>
                                {YEAR_OPTIONS.map(y => <Option key={y} value={y}>{y}</Option>)}
                            </Select>
                        </Form.Item>
                        <Form.Item name="is_new" label="Ngành mới" valuePropName="checked" style={{ width: 90 }}>
                            <Switch />
                        </Form.Item>
                        <Form.Item name="is_active" label="Hiển thị" valuePropName="checked" style={{ width: 90 }}>
                            <Switch />
                        </Form.Item>
                    </Space>

                    <Form.Item
                        name="ten_chuyen_nganh"
                        label="Tên chuyên ngành"
                        rules={[{ required: true, message: 'Bắt buộc' }]}
                    >
                        <Input placeholder="VD: Ngôn ngữ Anh" />
                    </Form.Item>

                    <Space style={{ width: '100%' }} size={12}>
                        <Form.Item name="ma_nganh" label="Mã ngành (Bộ GD)" style={{ width: 160 }}>
                            <Input placeholder="VD: 7220201" />
                        </Form.Item>
                        <Form.Item name="ten_nganh" label="Tên ngành chính" style={{ flex: 1 }}>
                            <Input placeholder="VD: Ngôn ngữ Anh" />
                        </Form.Item>
                    </Space>

                    <Text strong style={{ display: 'block', marginBottom: 8 }}>Điểm chuẩn năm trước</Text>
                    <Space wrap size={12} style={{ marginBottom: 16 }}>
                        {METHOD_COLS.map(({ key, label, max }) => (
                            <Form.Item key={key} name={key} label={label} style={{ marginBottom: 0 }}>
                                <InputNumber
                                    min={0} max={max} step={0.05} precision={2}
                                    style={{ width: 90 }}
                                    placeholder="—"
                                />
                            </Form.Item>
                        ))}
                    </Space>

                    <Form.Item name="to_hop_xt" label="Tổ hợp xét tuyển">
                        <TextArea
                            rows={2}
                            placeholder="VD: A00, A01, D01, D07"
                        />
                    </Form.Item>
                </Form>
            </Modal>
        </AdminLayout>
    );
};

export default AdminAdmissionPrograms;
